import { Auth as Cognito } from '@aws-amplify/auth';
import axios from 'axios';
import { getEnv } from '@smile-next/uikit';
import {
    CloudIdEmptyError,
    NetworkError,
    NotAuthorizedError,
    SSONotSettingError,
    TooManyRequestsError,
    UnexpectedError,
    UserNotFoundError,
} from '@/errors';
import { createLambdaError } from '@/errors/utils/createLambdaError';
import { getLambdaErrorCode } from '@/utils/getLambdaErrorCode';

export interface Auth {
    checkCurrentUser(): Promise<void>;
    loginWithNextAccount(cloudId?: string, loginId?: string, password?: string): Promise<void>;
    loginWithSSO(cloudId?: string): Promise<void>;
    signOut(): Promise<void>;
    hasAvailableServices(): Promise<boolean>;
}

export class CognitoAuth implements Auth {
    checkCurrentUser() {
        return Cognito.currentAuthenticatedUser().then((user) => {
            if (user != null) {
                return Promise.resolve();
            }
            throw new UserNotFoundError();
        });
    }

    loginWithNextAccount(cloudId?: string, loginId?: string, password?: string) {
        if (!cloudId) {
            // クラウドIDがないと使用できない
            throw new CloudIdEmptyError();
        }

        return axios
            .get(`/v1/auth-holder/user-pool?cloudId=${cloudId}`)
            .then((res) => {
                if (res.data.data) {
                    const { userPoolId, appClientId, ssoDomain } = res.data.data;
                    const env = getEnv();
                    Cognito.configure({
                        ...env.auth,
                        userPoolId,
                        userPoolWebClientId: appClientId,
                        oauth: {
                            ...env.auth.oauth,
                            domain: ssoDomain,
                        },
                    });
                }
            })
            .finally(() => {
                return (
                    Cognito.signIn(`${loginId}_${cloudId}`, password, { loginId: `${loginId}_${cloudId}` })
                        .then((user) => {
                            if (user != null) {
                                return Promise.resolve();
                            }
                            throw new UserNotFoundError();
                        })
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .catch((error: any) => {
                            const lambdaErrorCode = getLambdaErrorCode(error.message);
                            switch (error.code) {
                                case 'NotAuthorizedException':
                                case 'InvalidParameterException': // 指定したログインIDがクラウドIDに存在せず、ログインIDが128文字を超える場合
                                    throw new NotAuthorizedError();
                                case 'TooManyRequestsException':
                                    throw new TooManyRequestsError();
                                case 'NetworkError':
                                    throw new NetworkError();
                                case 'UserLambdaValidationException':
                                    throw createLambdaError(lambdaErrorCode);
                                default:
                                    throw new UnexpectedError();
                            }
                        })
                );
            });
    }

    async loginWithSSO(cloudId?: string) {
        if (!cloudId) {
            // クラウドIDがないと使用できない
            throw new CloudIdEmptyError();
        }

        let response;
        try {
            response = await axios.get(`/v1/auth-holder/user-pool?cloudId=${cloudId}`);
        } catch (error) {
            throw new NetworkError();
        }

        if (!response.data.data || !response.data.data.ssoEnabled) {
            throw new SSONotSettingError();
        }

        const { userPoolId, appClientId, ssoDomain } = response.data.data;

        const env = getEnv();
        Cognito.configure({
            ...env.auth,
            userPoolId,
            userPoolWebClientId: appClientId,
            oauth: {
                ...env.auth.oauth,
                domain: ssoDomain,
            },
        });

        try {
            await Cognito.federatedSignIn({ customProvider: cloudId });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            switch (error.code) {
                case 'TooManyRequestsException':
                    throw new TooManyRequestsError();
                case 'NetworkError':
                    throw new NetworkError();
                default:
                    throw new UnexpectedError();
            }
        }
    }

    signOut() {
        return Cognito.signOut();
    }

    /**
     * 2024年2月版で追加されるcustom:availableServices属性が存在するか
     */
    async hasAvailableServices() {
        const response = await Cognito.currentSession();
        const payload = response.getIdToken().decodePayload();
        return payload['custom:availableServices'] != null;
    }
}

export const cognitoAuth = new CognitoAuth();
